import { Link } from 'react-router-dom';
import type { Article } from '@/types';
import { relativeTime, formatViews } from '@/utils/format';

interface ArticleCardProps {
  article: Article;
  layout?: 'grid' | 'list';
  showSummary?: boolean;
}

export default function ArticleCard({ article, layout = 'grid', showSummary = true }: ArticleCardProps) {
  if (layout === 'list') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
      >
        <div className="w-28 h-20 sm:w-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="font-heading text-sm sm:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            {showSummary && (
              <p className="hidden sm:block mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.summary}</p>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{article.categoryName}</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-time-line" /> {relativeTime(article.publishTime)}
            </span>
            <span className="hidden sm:flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" /> {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-background-200">
        <img src={article.cover} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute left-3 top-3 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
          {article.categoryName}
        </span>
        {article.hot && (
          <span className="absolute right-3 top-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium flex items-center gap-1">
            <i className="ri-fire-line" /> 热
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        {showSummary && (
          <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed flex-1">{article.summary}</p>
        )}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-100 text-xs text-foreground-400">
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line" /> {relativeTime(article.publishTime)}
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <i className="ri-eye-line" /> {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}